import React from 'react';
import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import ContainerBox from 'common/components/layout/ContainerBox';
import * as RentalSelectors from 'selectors/RentalViewSelectors';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { getPricingString, notNullish, notUndefined } from 'common/utils/common';
import {
	getPaymentAmountsFromOrderPayment,
	PaymentAmounts,
} from 'common/modules/atoms/paymentAmounts';
import { useTranslation } from 'services/localization';
import { getTranslation } from 'common/modules/translations';
import useLocalFormat from 'hooks/useShopFormat';
import { getChannelName, getDepositStatusVariant } from '../utils';
import DetailSection from './DetailSection';
import { getBookingPaymentState } from 'common/modules/bookings';
import { DepositType, OrderInfo } from 'common/types';
import { getDepositStatus } from '../PaymentSection/DepositSection/utils';
import { hasOtherDeposit } from 'common/modules/atoms/deposit';
import { getChargeStatus } from '../PaymentSection/ChargeSection/utils';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import { getDepositStatusTranslation } from 'services/translations';

const GeneralDetails = () => {
	const orderInfo = useSelector(RentalSelectors.orderInfo)!;
	const lang = useSelector(LanguageSelectors.language);
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);

	const { localFormat } = useLocalFormat();
	const { t } = useTranslation();

	const currency = getCurrencyObjectFromCurrencyCode(orderInfo.pricing.currency, shopCurrency);

	const paymentAmount = getPaymentAmountsFromOrderPayment(
		orderInfo.pricing.total,
		orderInfo.pricing.currency,
		orderInfo.charge,
	);

	const depositPaymentAmount = !!orderInfo.pricing.deposit
		? getPaymentAmountsFromOrderPayment(
				orderInfo.pricing.deposit.value,
				orderInfo.pricing.currency,
				orderInfo.deposit,
		  )
		: null;

	const depositPricing = orderInfo.pricing.deposit;
	const depositType = orderInfo.pricing.deposit?.type;
	const hasDeposit = !!depositType;
	const hasItemDeposit = hasOtherDeposit(depositPricing);

	const getDepositStatusText = (
		depositType: DepositType | undefined,
		paymentAmount: PaymentAmounts | null,
	) => {
		if (!paymentAmount) return '';
		if (depositType === 'no-deposit') {
			return t('common:payment.noDeposit');
		}
		if (depositType === 'other') {
			return t('common:payment.itemDeposit', 'Item deposit');
		}
		return t('common:payment.deposit', 'Deposit');
	};

	const getPaymentStatusText = (orderInfo: OrderInfo) => {
		const paymentState = getBookingPaymentState(orderInfo);
		switch (paymentState) {
			case 'PAID':
				return t('common:payment.paid');
			case 'UNPAID':
				return t('common:payment.unpaid');
			case 'REFUNDED':
				return t('common:payment.refunded');
		}
	};

	const getDepositFullPricingString = (depositPaymentAmount: PaymentAmounts | null) => {
		if (depositPaymentAmount === null) {
			return getPricingString(0, currency);
		}

		const depositStatus = getDepositStatus(depositPaymentAmount).status;

		return depositStatus === 'charged'
			? getPricingString(depositPaymentAmount.total ?? 0, currency)
			: `${getPricingString(
					depositPaymentAmount.total ?? 0,
					currency,
			  )} (${getDepositStatusTranslation(depositStatus, t)})`;
	};

	const getDepositPartialPricingString = (
		depositType: DepositType | undefined,
		depositPaymentAmount: PaymentAmounts | null,
	) => {
		if (depositPaymentAmount === null) {
			return getPricingString(0, currency);
		}

		const totalToReserve = depositPaymentAmount.total;
		const reservedAmount = depositPaymentAmount.reserved;
		const chargedAmount = depositPaymentAmount.paid;
		const unreservedAmount = depositPaymentAmount.unreserved;

		const pricingAmount = !!chargedAmount
			? chargedAmount
			: !!reservedAmount && (reservedAmount !== totalToReserve || depositType !== 'payment')
			? reservedAmount
			: !!unreservedAmount && unreservedAmount !== totalToReserve && unreservedAmount > 0
			? Math.abs(unreservedAmount)
			: undefined;

		if (!pricingAmount) return undefined;

		const depositStatus = getDepositStatus(depositPaymentAmount).status;
		const statusText = `(${getDepositStatusTranslation(depositStatus, t)})`;

		return `${getPricingString(pricingAmount, currency)} ${statusText}`;
	};

	const generalDetailsItems = [
		{
			label: t('orderSummaryDialog.generalDetails.orderCreated', 'Order created'),
			values: [localFormat(orderInfo.created, 'DD.MM.YYYY, HH:mm')],
		},
		{
			label: t('channel'),
			values: [getChannelName(orderInfo.channel, t)].filter(notNullish),
		},
		{
			label: t('common:times.startDate'),
			values: [localFormat(orderInfo.startDate, 'ddd D.M.YYYY HH:mm')],
		},
		{
			label: t('common:shopLocation.store'),
			values: [orderInfo.startLocation.name],
		},
		{
			label: getPaymentStatusText(orderInfo),
			values: [getPricingString(orderInfo.pricing.total, currency)],
			variant: paymentAmount ? getChargeStatus(paymentAmount).variant : 'neutral',
		},
		hasDeposit
			? {
					label: getDepositStatusText(depositType, depositPaymentAmount),
					values: [
						hasItemDeposit
							? depositPricing?.additionalInfo
							: [
									getDepositFullPricingString(depositPaymentAmount),
									getDepositPartialPricingString(depositType, depositPaymentAmount),
							  ],
					]
						.flat()
						.filter(notUndefined),
					variant: !!depositPaymentAmount
						? getDepositStatusVariant(depositType, depositPaymentAmount)
						: undefined,
			  }
			: undefined,
		{
			label: t('orderSummaryDialog.generalDetails.storeNote', 'Store note'),
			values: orderInfo.additionalInformation ? [orderInfo.additionalInformation] : undefined,
		},
		{
			label: getTranslation(orderInfo.bookingComment?.label, lang),
			values: orderInfo.bookingComment?.value ? [orderInfo.bookingComment.value] : undefined,
		},
	]
		.filter(notUndefined)
		.filter((item) => notUndefined(item.values));

	return (
		<ContainerBox>
			<Typography mb={2} variant="h6">
				{t('orderSummaryDialog.generalDetails.header', 'General')}
			</Typography>
			<DetailSection items={generalDetailsItems} />
		</ContainerBox>
	);
};

export default GeneralDetails;
